import { inject, injectable } from 'tsyringe';
import { ICache } from '../../Cache';
import { OrderStatus } from '../../constants/lookups';
import { ApiInjectToken, MiddlewareInjectToken } from '../../constants/tokens';
import {
    Account,
    Apis,
    AxosCryptoPosition,
    AxosCryptoPositionWithExtraInfo,
    AxosPortfolio,
    CryptoAccountInfo,
    CryptoOrderHistory,
    CryptoOrderHistoryWithExtraInfo,
    CryptoStablecoinTransferStatus,
    CryptoStablecoinTransferStatusWithExtraInfo,
    CryptoTradingHistory,
    CryptoTradingHistoryWithExtraInfo,
    MiddlewareConfig,
    PaginationData,
    Quote,
} from '../../types';
import { getUuid, multiply, parseCryptoSymbol, sumBy } from '../../utils';
import { AccountApi } from '../AccountApi';
import { BranchApi } from '../BranchApi';
import { GroupApi } from '../GroupApi';
import { ModelApi } from '../ModelApi';
import { QuoteApi } from '../QuoteApi';
import { TipranksApi } from '../TipranksApi';
import { UserApi } from '../UserApi';
import { AXOS_COIN } from './cryptoLookups';
import { formatCryptoAssetWithAxx } from './utils';

@injectable()
export class AxosCryptoAccountApi extends AccountApi {
    constructor(
        @inject(MiddlewareInjectToken.Apis) apis: Apis,
        @inject(MiddlewareInjectToken.Config) config: MiddlewareConfig,
        @inject(MiddlewareInjectToken.Cache) cache: ICache,
        @inject(ApiInjectToken.BranchApi) protected override readonly branchApi: BranchApi,
        @inject(ApiInjectToken.ModelApi) protected override readonly modelApi: ModelApi,
        @inject(ApiInjectToken.GroupApi) protected override readonly groupApi: GroupApi,
        @inject(ApiInjectToken.QuoteApi) protected override readonly quoteApi: QuoteApi,
        @inject(ApiInjectToken.UserApi) protected override readonly userApi: UserApi,
        @inject(ApiInjectToken.TipranksApi) protected override readonly tipranksApi: TipranksApi,
    ) {
        super(apis, config, cache, branchApi, modelApi, groupApi, quoteApi, userApi, tipranksApi);
    }

    async getAxosAccounts(): Promise<Account[]> {
        const response = await this.userApi.getUserInfo();
        return response.accountList.filter((account) => account.cryptoEnabled);
    }

    async getAxosCryptoAccountInfo(accountNumber: string): Promise<CryptoAccountInfo> {
        const response = await this.apis.middleware.getApiClient().get<any>('axos/crypto/accounts', {
            params: { accountNumber },
        });
        // Axos layer wraps data in entity array
        return response.data.entity[0]!;
    }

    async getAxosAccountCryptoBuyingPower(accountNumber: string): Promise<number> {
        const assets = await this.getAxosCryptoPortfolio({ accountNumber });
        const entries: AxosCryptoPositionWithExtraInfo[] = assets.items;
        return entries.filter((holding) => holding.asset.toUpperCase() === AXOS_COIN)[0]?.value || 0;
    }

    async getAxosCryptoPortfolio({
        accountNumber,
        pageStart,
        pageSize,
    }: {
        accountNumber?: string;
        pageStart?: number;
        pageSize?: number;
    }): Promise<AxosPortfolio> {
        const response = await this.apis.middleware.getApiClient().get<any>('axos/crypto/balances', {
            params: { accountNumber, pageStart, pageSize },
        });
        const assetData: AxosCryptoPosition[] = response.data.entity?.[0].assets
            ? response.data.entity?.[0]?.assets
            : [];
        const assetInstruments = assetData.map((asset) => asset.asset);
        const formattedAssetInstruments = assetInstruments.map(formatCryptoAssetWithAxx).join(', ');
        const { data: instrumentQuotes } = await this.apis.orbis.quotes.crypto({
            symbols: formattedAssetInstruments,
        });

        const assets = assetData.map((position) => {
            const quote = instrumentQuotes.find((quote: Quote) => quote.baseCurrency === position.asset.toUpperCase());

            return {
                symbol: formatCryptoAssetWithAxx(position.asset),
                // We use asset for logo. Column cannot have duplicate ID.
                asset: position.asset,
                value:
                    position.asset.toUpperCase() === AXOS_COIN
                        ? multiply(position.balance, 1)
                        : multiply(position.balance, quote?.lastPrice || 0),
                balance: position.balance,
                lastPrice: quote?.lastPrice || 0,
                changePercent: quote?.changePercent || 0,
                quote: {
                    symbol: position.asset,
                    crypto: true,
                },
            };
        });
        return {
            itemCount: response.data.totalCount,
            // Axos API returns assets even when quantity is 0. They requested to filter this for the time being. Might change when more coins are available.
            items: assets.filter((asset) => asset.balance !== 0),
            total: sumBy(assets, (asset) => asset.value),
        };
    }

    async getAccountCryptoAccountOrderHistory(
        accountNumber: string,
        pageStart?: number,
        pageSize?: number,
        status?: OrderStatus[],
    ): Promise<PaginationData<CryptoOrderHistoryWithExtraInfo>> {
        const [availableCoins, response] = await Promise.all([
            this.quoteApi.getAvailableAssets(),
            this.apis.middleware
                .getApiClient()
                .get<{ entity: CryptoOrderHistory[]; totalCount: number }>('axos/crypto/trading/orders/history', {
                    params: { accountNumber, pageStart, pageSize, status: status?.join(',') },
                }),
        ]);

        if (response.data.entity.length) {
            return {
                totalCount: response.data.totalCount,
                // Axos layer wraps data within entity object
                data: response.data.entity.map((item: any) => {
                    const coin = availableCoins.find(
                        (availableCoin) => availableCoin.symbol === parseCryptoSymbol(item.instrument).symbol,
                    );
                    return {
                        ...item,
                        symbol: item.instrument,
                        nameWithIcon: `${coin?.name} (${coin?.icon})` || '',
                        quote: {
                            crypto: true,
                        },
                    };
                }),
            };
        } else {
            return { data: [], totalCount: 0 };
        }
    }

    async getAccountCryptoAccountTradingHistory(
        accountNumber: string,
        pageStart?: number,
        pageSize?: number,
    ): Promise<{ data: CryptoTradingHistoryWithExtraInfo[]; totalCount: number }> {
        const [availableCoins, response] = await Promise.all([
            this.quoteApi.getAvailableAssets(),
            this.apis.middleware
                .getApiClient()
                .get<{ entity: CryptoTradingHistory[]; totalCount: number }>('axos/crypto/trading/trades/history', {
                    params: { accountNumber, pageStart, pageSize },
                }),
        ]);
        if (response.data.entity.length) {
            // Axos layer wraps data within entity object
            return {
                totalCount: response.data.totalCount,
                data: response.data.entity.map((item: any) => {
                    const coin = availableCoins.find(
                        (availableCoin) => availableCoin.symbol === parseCryptoSymbol(item.instrument).symbol,
                    );
                    return {
                        ...item,
                        symbol: item.instrument,
                        nameWithIcon: `${coin?.name} (${coin?.icon})` || '',
                        quote: {
                            crypto: true,
                        },
                    };
                }),
            };
        } else {
            return { data: [], totalCount: 0 };
        }
    }

    async getAccountCryptoOpenOrders(
        accountNumber: string,
        pageStart?: number,
        pageSize?: number,
    ): Promise<{ data: CryptoOrderHistoryWithExtraInfo[]; totalCount: number }> {
        const status = [OrderStatus.Open, OrderStatus.PendingCancel];
        const response = await this.apis.middleware.getApiClient().get<any>('axos/crypto/trading/orders', {
            params: {
                accountNumber,
                status: status.join(','),
                pageStart,
                pageSize,
            },
        });

        if (response.data.entity) {
            return {
                totalCount: response.data.totalCount,
                data: response.data.entity.map((item: CryptoTradingHistory) => ({
                    ...item,
                    symbol: item.instrument,
                    asset: item.instrument,
                    quote: {
                        crypto: true,
                    },
                })),
            };
        } else {
            return { data: [], totalCount: 0 };
        }
    }

    async purchaseCryptoStablecoins(accountNumber: string, amount: number): Promise<{ reason: string }> {
        const response = await this.apis.middleware
            .getApiClient()
            .post<any>('axos/crypto/transfers/stablecoins/purchase', {
                requestId: getUuid(),
                requests: [{ accountNumber, amount }],
                _accountNumber: accountNumber,
            });

        // Axos layer wraps data within entity array. This endpoints supports batch requests, but for this instance, we only send one, so we take the first object in entity array.
        return response.data.entity[0];
    }

    async redeemCryptoStablecoins(accountNumber: string, amount: number): Promise<{ reason: string }> {
        const response = await this.apis.middleware
            .getApiClient()
            .post<any>('axos/crypto/transfers/stablecoins/redemption', {
                requestId: getUuid(),
                requests: [{ accountNumber, amount }],
            });

        // Axos layer wraps data within entity array. This endpoints supports batch requests, but for this instance, we only send one, so we take the first object entity array.
        return response.data.entity[0];
    }

    async getAccountCryptoStablecoinTransferStatuses(
        accountNumber: string,
        pageStart?: number,
        pageSize?: number,
    ): Promise<{ data: CryptoStablecoinTransferStatusWithExtraInfo[]; totalCount: number }> {
        const response = await this.apis.middleware.getApiClient().get<{
            entity: CryptoStablecoinTransferStatus[];
            totalCount: number;
        }>('axos/crypto/transfers', {
            params: {
                accountNumber,
                pageStart,
                pageSize,
            },
        });
        if (response.data.entity.length) {
            return {
                totalCount: response.data.totalCount,
                // Axos layer wraps data within entity object.
                data: response.data.entity.map((item: any) => ({
                    ...item,
                    symbol: item.asset,
                })),
            };
        } else {
            return { data: [], totalCount: 0 };
        }
    }

    async getAccountCryptoAvailablePdfStatementDates(accountNumber: string): Promise<{ from: Date; to: Date }> {
        const response = await this.apis.middleware
            .getApiClient()
            .get<any>('axos/crypto/accounts/account-reports/info', {
                params: { accountNumber },
            });
        const availableDates: { from: Date; to: Date } = response.data.entity[0].reportsAvailability;
        return availableDates;
    }

    async generateAccountCryptoPdfStatement(accountNumber: string, date: string): Promise<Blob> {
        const response = await this.apis.middleware
            .getApiClient()
            .get<any>('axos/crypto/accounts/account-reports/pdf', {
                responseType: 'blob',
                params: { reportDate: date, accountNumber },
            });
        return response.data;
    }

    async getAccountCryptoOrdersCsv(accountNumber: string): Promise<Blob> {
        const response = await this.apis.middleware.getApiClient().get<any>('axos/crypto/trading/orders/csv', {
            responseType: 'blob',
            params: { accountNumber },
        });
        return response.data;
    }

    async getAccountCryptoTransfersCsv(accountNumber: string): Promise<Blob> {
        const response = await this.apis.middleware.getApiClient().get<any>('axos/crypto/transfers/csv', {
            responseType: 'blob',
            params: { accountNumber },
        });
        return response.data;
    }
}
